<script lang="ts">
    import { onMount } from 'svelte'
    import { replace } from 'svelte-spa-router'
    import { get } from 'svelte/store'
    import type DataProvider from '../services/data.provider'
    import type ServiceProvider from '../services/service.provider'
    import { loadDaysList } from '../api/day.api'
    import { loadTimeList } from '../api/time.api'
    import Breadcrumb from '../components/Breadcrumb.svelte'
    import type { IDay } from '../interfaces/day.interface'
    import type { ITime } from '../interfaces/time.interface'
    import { formatDate } from '../../../core/utils/date.util'
    import { t } from '../lib/i18n/t'
    import { currentLang } from '../lib/i18n/lang'

    export let dataProvider: DataProvider
    export let serviceProvider: ServiceProvider
    export let params: { id: string }

    let days: IDay[] = []
    let times: ITime[] = []
    let dateValue = dataProvider.date
    let timeValue = dataProvider.time
    const activityId = Number(params.id)

    let processingTimes = false
    let processingDays = true

    const selectDate = (selectedDate: string) => {
        times = []
        dataProvider.date = selectedDate
        processingTimes = true
        if (window.outerWidth > 930) {
            // eslint-disable-next-line no-undef
            window.scrollTo({ top: 0, behavior: "smooth" })
        }
        loadTimeList(activityId, dataProvider.branchId, dataProvider.date)
            .then((data) => {
                times = data.seznam.filter((time) => time.stav)
                times = times.filter(
                    (time, pos) =>
                        times.findIndex((time2) => time.cas == time2.cas) == pos
                )
            })
            .finally(() => (processingTimes = false))
    }
    const selectTime = (selectedTime: string) => {
        dataProvider.activityId = activityId
        dataProvider.time = selectedTime
    }

    onMount(() => {
        // Note: on Brno-EN is only 1 service on zu branch, so other services should not be accessible
        if (get(currentLang) === 'en' && dataProvider.branchId === 4 && activityId !== 86) {
            // Note: this row not working. svelte-spa-router needs bigger url change:
            // replace('/service/86')
            // This row is ok and the replace('/service/86') is in ActivitySelector:
            replace('/')
            return
        }

        // Note: on Brno (cs) is also available service 86 on zu branch, but it should redirect to EN web
        if (get(currentLang) !== 'en' && dataProvider.branchId === 4 && activityId === 86) {
            document.location.href = 'https://en.brno.cz/w/online-ordering-zu#/service/86'
            return
        }

        dataProvider.activityId = activityId
        void serviceProvider.individualFormService.load(activityId)
        loadDaysList(activityId, dataProvider.branchId)
            .then(
                (data) => (days = data.seznam.filter((date) => date.stav === 1))
            )
            .finally(() => (processingDays = false))

        if (timeValue && dateValue) selectDate(dateValue)
    })

    $: activity = serviceProvider.activityService.getActivityById(dataProvider.activityId)
</script>

<Breadcrumb dateTimeSection={true} {serviceProvider} {dataProvider}/>
<div class="bno-container bno-container--centered">
    <div class="bno-container__content">
        <h2>{t('dateTimeSelector.jakyTerminVamVyhovuje')}</h2>
        {#if activity}
            <h3 style="margin-top: 1rem">{activity.nazev}</h3>
        {/if}
        {#if dataProvider.numberOfFollowingDays || dataProvider.occupancyCountersId}
            {#if dataProvider.occupancyCountersId}
                <p>
                    <strong>
                        {t('dateTimeSelector.vyrizeniZadostiJeMozneIBezElObj')}
                        <a
                            target="_blank"
                            rel="nofollow"
                            href="https://objednani.brno.cz/mon/index.html?id={dataProvider.occupancyCountersId}">
                            {t('dateTimeSelector.aktualniObsazenostPrepazek')}
                        </a>.
                    </strong>
                </p>
            {/if}
            {#if dataProvider.numberOfFollowingDays}
                <p>
                    {t('dateTimeSelector.nizeNajdeteVolneTerminy', {
                        numberOfFollowingDays: dataProvider.numberOfFollowingDays
                    })}
                    {t('dateTimeSelector.pokudNebylyNalezenyDostupneTerminy', {
                        numberOfFollowingDays: dataProvider.numberOfFollowingDays
                    })}
                </p>
            {/if}
        {/if}
        <ul class="bno-grid bno-grid--x2 bno-grid--gap-40 bno-list--plain">
            <li class="bno-grid__tile">
                <div
                        class="bno-form-control bno-form-control--custom bno-form-control--booking"
                >
                    {#if processingDays}
                        <p>{t('dateTimeSelector.nacitamDostupneDny')}...</p>
                    {:else if days.length}
                        <ul class="bno-form-group bno-form-group--booking">
                            {#each days as day}
                                <li class="bno-form-group__item">
                                    <input
                                            class="bno-form-control__radio"
                                            type="radio"
                                            aria-required="true"
                                            name="dateSelector"
                                            id="dateSelector-{day.datum}"
                                            bind:group={dateValue}
                                            value={day.datum}
                                            on:change={() =>
                                            selectDate(day.datum)}
                                    />
                                    <label
                                            class="bno-form-control__label"
                                            for="dateSelector-{day.datum}"
                                    >
                                        {@html formatDate(day.datum, true, get(currentLang))}
                                    </label>
                                    <span
                                            class="bno-form-control__indicator"
                                    />
                                </li>
                            {/each}
                        </ul>
                    {:else}
                        {t('dateTimeSelector.nebylyNalezenyZadneDostupneDny')}
                    {/if}
                </div>
            </li>
            {#if dataProvider.date}
                <li class="bno-grid__tile">
                    <div
                            class="bno-form-control bno-form-control--custom bno-form-control--inline bno-form-control--booking"
                    >
                        {#if processingTimes}
                            <p>{t('dateTimeSelector.nacitamDostupneCasy')}...</p>
                        {:else if times.length}
                            <ul
                                    class="bno-form-group bno-form-group--inline bno-form-group--booking"
                            >
                                {#each times as time}
                                    <li class="bno-form-group__item">
                                        <input
                                                class="bno-form-control__radio"
                                                type="radio"
                                                name="timeSelector"
                                                bind:group={timeValue}
                                                id="timeSelector-{time.cas}"
                                                value={time.cas}
                                                on:change={() =>
                                                selectTime(time.cas)}
                                        />
                                        <label
                                                class="bno-form-control__label"
                                                for="timeSelector-{time.cas}"
                                        >
                                            {time.cas}
                                        </label>
                                        <span
                                                class="bno-form-control__indicator"
                                        />
                                    </li>
                                {/each}
                            </ul>
                        {:else}
                            {t('dateTimeSelector.nebylyNalezenyZadneDostupneCasy')}
                        {/if}
                    </div>

                    {#if dataProvider.date && dataProvider.time}
                        <!-- TODO: do it better https://jirasw.asseco-ce.com/browse/SMBWEB-1195 -->
                        {#if dataProvider.displayOsc === '1' && (dataProvider.branchId != 2 || dataProvider.activityId == 37)}
                            <div class="bno-alert-light bno-alert-light--info">
                                <span class="bno-icon bno-icon--info"></span>
                                {t('dateTimeSelector.objednanimJednohoTerminuRezervujeteCasPouzeProOdbaveniJedneOsoby')}
                            </div>
                        {/if}
                        <button
                                class="bno-button bno-button--primary"
                                on:click={() => replace(`/${t('page.kontakt')}`)}
                        >
                            {t('dateTimeSelector.pokracovat')}
                        </button>
                    {/if}
                </li>
            {/if}
        </ul>
    </div>
</div>
